import { useState, useEffect } from 'react';
import {
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  BellAlertIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  BoltIcon,
  Squares2X2Icon,
  ChartBarIcon,
  FolderIcon,
  StarIcon,
  LightBulbIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  CpuChipIcon,
  CircleStackIcon,
  BeakerIcon,
  CheckCircleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import BrainIcon from '../components/common/BrainIcon';

export function useModules() {
  const [sidebarGroups, setSidebarGroups] = useState([]);
  const [enabledModules, setEnabledModules] = useState([]);
  const [availableModules, setAvailableModules] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // For now, use core modules directly since API endpoints don't exist yet
    // TODO: Replace with actual API calls when backend module endpoints are implemented
    setSidebarGroups(getCoreModules());
    setEnabledModules(['core', 'operational-risk', 'cyber-risk']); // Mock enabled modules
    setAvailableModules({}); // Mock available modules
    setLoading(false);
  }, []);

  const fetchModuleConfig = async () => {
    try {
      setLoading(true);
      
      // Future: Fetch enabled modules and sidebar config
      // For now, just use core modules
      setSidebarGroups(getCoreModules());
      setEnabledModules(['core', 'operational-risk', 'cyber-risk']);
      setAvailableModules({});
      
    } catch (error) {
      console.error('Error fetching module config:', error);
      // Fallback to core modules only
      setSidebarGroups(getCoreModules());
    } finally {
      setLoading(false);
    }
  };

  const getCoreModules = () => {
    // Core RISX.AI modules (always available)
    return [
      {
        "label": "Risks",
        "icon": ExclamationTriangleIcon,
        "items": [
          {"label": "Taxonomy", "to": "/taxonomy", "icon": DocumentTextIcon},
          {"label": "Risk Library", "to": "/risk-library", "icon": FolderIcon},
          {"label": "Key Risk Indicators", "to": "/kri-library", "icon": ArrowTrendingUpIcon},
          {"label": "Assessments (placeholder)", "to": "/assessments", "icon": ClipboardDocumentListIcon},
        ],
      },
      {
        "label": "Compliance",
        "icon": DocumentTextIcon,
        "items": [
          {"label": "Regulations", "to": "/compliance/regulations", "icon": FolderIcon},
          {"label": "Obligations", "to": "/compliance/obligations", "icon": ClipboardDocumentListIcon},
          {"label": "Policies", "to": "/compliance/policies", "icon": DocumentTextIcon},
          {"label": "Product Assessment", "to": "/compliance/product-assessment", "icon": StarIcon},
          {"label": "Compliance Dashboard", "to": "/compliance/dashboard", "icon": ChartBarIcon},
        ],
      },
      {
        "label": "Controls",
        "icon": ShieldCheckIcon,
        "items": [
          {"label": "Controls Dashboard", "to": "/controls", "icon": Squares2X2Icon},
          {"label": "Controls Library", "to": "/control-library", "icon": FolderIcon},
          {"label": "Control Intelligence", "to": "/control-lattice", "icon": CpuChipIcon},
          {"label": "Advanced Analytics", "to": "/advanced-analytics", "icon": SparklesIcon},
        ],
      },
      {
        "label": "Testing",
        "icon": BeakerIcon,
        "items": [
          {"label": "Testing Dashboard", "to": "/testing", "icon": Squares2X2Icon},
          {"label": "Test Plans", "to": "/testing/plans", "icon": ClipboardDocumentListIcon},
          {"label": "Test Execution", "to": "/testing/execution", "icon": CheckCircleIcon},
          {"label": "Evidence Management (placeholder)", "to": "/testing/evidence", "icon": EyeIcon},
          {"label": "Campaigns", "to": "/testing/campaigns", "icon": CalendarIcon},
          {"label": "Real-Time Monitor", "to": "/testing/monitor", "icon": BoltIcon},
          {"label": "Advanced Analytics", "to": "/testing/analytics", "icon": ChartBarIcon},
        ],
      },
      {
        "label": "Incidents",
        "icon": BellAlertIcon,
        "items": [
          {"label": "Incident Library", "to": "/incident-library", "icon": BellAlertIcon},
          {"label": "External Feeds (placeholder)", "to": "/incidents/external", "icon": CircleStackIcon},
          {"label": "Analytics", "to": "/incidents/analytics", "icon": ChartBarIcon},
        ],
      },
      {
        "label": "Issues",
        "icon": CalendarIcon,
        "items": [
          {"label": "Issues Library", "to": "/issue-library", "icon": CalendarIcon},
        ],
      },
      {
        "label": "Action Plans",
        "icon": ClipboardDocumentListIcon,
        "items": [
          {"label": "Action Plans Library", "to": "/action-plans", "icon": ClipboardDocumentListIcon},
        ],
      },
      {
        "label": "Jobs",
        "icon": BoltIcon,
        "items": [
          {"label": "Jobs To Do", "to": "/jobs", "icon": BoltIcon},
          {"label": "Visual Board", "to": "/jobs/enhanced", "icon": Squares2X2Icon},
          {"label": "Scheduled / Recurring (placeholder)", "to": "/jobs?scheduled=true", "icon": CalendarIcon},
        ],
      },
      {
        "label": "Analytics",
        "icon": ChartBarIcon,
        "items": [
          {"label": "Analytics Dashboard (placeholder)", "to": "/analytics", "icon": ArrowTrendingUpIcon},
          {"label": "Intelligent Dashboards (placeholder)", "to": "/intelligent-dashboards", "icon": SparklesIcon},
          {"label": "Interactive Intelligence", "to": "/interactive-intelligence", "icon": CpuChipIcon},
          {"label": "Relationship Domain Detection", "to": "/relationship-domain-detection", "icon": CircleStackIcon},
        ],
      },
    ];
  };

  const isModuleEnabled = (moduleName) => {
    return enabledModules.includes(moduleName);
  };

  return {
    sidebarGroups,
    enabledModules,
    availableModules,
    loading,
    isModuleEnabled,
    refreshModules: fetchModuleConfig
  };
} 